import {UnlinkedXeroInvoice, UnlinkedXeroInvoices} from '../../../components/insights/invoicing/importFromXero';

/*
 * The invoices ticked in the import modal. A tick keeps a copy of its row, so it survives a reload of
 * the list for another range and can still be counted and handed to Create. Every function returns a
 * new map (or the same one when nothing changed) and never changes the one it is given.
 */

/** A list row, with the client match the Client match story adds. Absent until then. */
export type SelectableInvoice = UnlinkedXeroInvoice & {clientMatch?: 'matched' | 'newClient' | 'review'};

/** Ticked rows by invoiceKey. */
export type SelectedInvoices = ReadonlyMap<string, SelectableInvoice>;

/** Whether a row can be ticked. Every row can; the Client match story narrows it for CLIENT_CREATE. */
export type CanSelect = (row: SelectableInvoice) => boolean;

export const canSelectAll: CanSelect = () => true;

/**
 * One Xero contact: contact ids are per organisation too. Kept here, in this module with no imports
 * of its own, so the Client match code can use it without the selection depending on that code.
 */
export const contactKey = (row: {tenantId: string; contactId: string}): string => `${row.tenantId}:${row.contactId}`;

/** Ids are per Xero organisation, so the tenant is part of the key. */
export const invoiceKey = (row: Pick<UnlinkedXeroInvoice, 'tenantId' | 'invoiceId'>): string =>
	`${row.tenantId}:${row.invoiceId}`;

export const toggleInvoice = (selected: SelectedInvoices, row: SelectableInvoice): SelectedInvoices => {
	const next = new Map(selected);
	const key = invoiceKey(row);
	if (next.has(key)) {
		next.delete(key);
	} else {
		next.set(key, row);
	}
	return next;
};

/**
 * The header checkbox: ticks every visible row that can be ticked, or unticks them all when they are
 * all ticked already. Ticks outside the view are never touched.
 */
export const toggleVisible = (
	selected: SelectedInvoices,
	visible: readonly SelectableInvoice[],
	canSelect: CanSelect
): SelectedInvoices => {
	const eligible = visible.filter(canSelect);
	if (eligible.length === 0) {
		return selected;
	}
	const untick = eligible.every(row => selected.has(invoiceKey(row)));
	const next = new Map(selected);
	eligible.forEach(row => {
		if (untick) {
			next.delete(invoiceKey(row));
		} else {
			next.set(invoiceKey(row), row);
		}
	});
	return next;
};

export const headerState = (
	selected: SelectedInvoices,
	visible: readonly SelectableInvoice[],
	canSelect: CanSelect
): boolean | 'indeterminate' => {
	const eligible = visible.filter(canSelect);
	const ticked = eligible.filter(row => selected.has(invoiceKey(row))).length;
	if (ticked === 0) {
		return false;
	}
	return ticked === eligible.length ? true : 'indeterminate';
};

/**
 * Applies a newer answer to the ticks. A ticked invoice in the answer gets its newer row. One missing
 * from it is dropped only when that proves it can no longer be imported: its organisation answered
 * ok (a complete read) and its date lies in the range this answer is for, bounds included. Anything
 * else (another range, an organisation not read) keeps the tick.
 */
export const refreshSelection = (
	selected: SelectedInvoices,
	answer: UnlinkedXeroInvoices,
	range: {startDate: string; endDate: string}
): SelectedInvoices => {
	const answered = new Map(answer.invoices.map(row => [invoiceKey(row), row]));
	const readInFull = new Set(answer.organisations.filter(org => org.status === 'ok').map(org => org.tenantId));
	let changed = false;
	const next = new Map<string, SelectableInvoice>();
	selected.forEach((row, key) => {
		const newer = answered.get(key);
		if (newer) {
			changed = changed || newer !== row;
			next.set(key, newer);
		} else if (readInFull.has(row.tenantId) && range.startDate <= row.date && row.date <= range.endDate) {
			changed = true;
		} else {
			next.set(key, row);
		}
	});
	return changed ? next : selected;
};

/**
 * total: every tick. outsideView: ticks not among the visible rows. newClients: distinct ticked Xero
 * contacts the Client match story marks as new clients; matched, review and unmarked rows never count.
 */
export const selectionCounts = (
	selected: SelectedInvoices,
	visible: readonly SelectableInvoice[]
): {total: number; outsideView: number; newClients: number} => {
	const tickedVisible = new Set(visible.map(invoiceKey).filter(key => selected.has(key))).size;
	const newClients = new Set<string>();
	selected.forEach(row => {
		if (row.clientMatch === 'newClient') {
			newClients.add(contactKey(row));
		}
	});
	return {total: selected.size, outsideView: selected.size - tickedVisible, newClients: newClients.size};
};
